import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  History, MessageCircle, Trash2, ChevronRight, Clock,
  Search, Plus, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getConversations, deleteConversation } from '@/services/patientWorkflowService';

const ConversationHistory = ({ patientId, role, onSelect, onNewChat, activeConversationId }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const loadConversations = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await getConversations(patientId, role);
      setConversations(res.conversations || []);
    } catch {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadConversations();
  }, [isOpen, patientId, role]);

  const handleDelete = async (e, convId) => {
    e.stopPropagation();
    try {
      await deleteConversation(patientId, convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch { /* ignore */ }
  };

  const filtered = conversations.filter(c =>
    !search || (c.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => setIsOpen(!isOpen)}
        title="Conversation history"
      >
        <History className="h-3.5 w-3.5" />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1 w-72 bg-background border rounded-xl shadow-xl z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="px-3 py-2.5 border-b bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-foreground">Past Conversations</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => { onNewChat(); setIsOpen(false); }}
                >
                  <Plus className="h-3 w-3 mr-1" />New
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search conversations..."
                  className="h-7 text-xs pl-7 rounded-md"
                />
              </div>
            </div>

            {/* List */}
            <ScrollArea className="max-h-64">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  {search ? 'No matching conversations' : 'No past conversations'}
                </div>
              ) : (
                <div className="py-1">
                  {filtered.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => { onSelect(conv.id); setIsOpen(false); }}
                      className={`w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-muted/50 transition-colors group ${
                        conv.id === activeConversationId ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                      }`}
                    >
                      <MessageCircle className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {conv.title || 'Untitled conversation'}
                        </p>
                        {conv.last_message && (
                          <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                            {conv.last_message}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5" />
                            {formatTime(conv.last_message_at || conv.created_at)}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {conv.message_count || 0} msgs
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDelete(e, conv.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </button>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConversationHistory;
